import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { reassignmentProblemText } from './reassignmentPresentation';

function operationId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

export function useBookingReassignment({
  bookingId,
  targetPropertyId,
  reason,
  open,
  onCompleted,
}: {
  bookingId: string;
  targetPropertyId: string;
  reason: string;
  open: boolean;
  onCompleted?: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [commandId, setCommandId] = useState(operationId);
  const [problem, setProblem] = useState<string | null>(null);
  const preview = useQuery({
    queryKey: ['bookings', 'reassignment-preview', bookingId, targetPropertyId],
    queryFn: () => api.bookings.reassignmentPreview(bookingId, targetPropertyId),
    enabled: open && !!targetPropertyId,
    retry: false,
  });

  useEffect(() => {
    if (!preview.error) return;
    let current = true;
    void toApiError(preview.error).then((error) => {
      if (current) setProblem(reassignmentProblemText(error));
    });
    return () => {
      current = false;
    };
  }, [preview.error]);

  const apply = useMutation({
    networkMode: 'always',
    mutationFn: () =>
      api.bookings.createReassignment(bookingId, {
        operationId: commandId,
        targetPropertyId,
        swapBookingId: preview.data?.swapBookingId ?? null,
        reason: reason.trim() || null,
      }),
    onSuccess: async () => {
      setProblem(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['calendar'] }),
        queryClient.invalidateQueries({ queryKey: ['bookings', 'detail', bookingId] }),
        queryClient.invalidateQueries({ queryKey: ['bookings', 'reassignments', bookingId] }),
      ]);
      toast.success(preview.data?.kind === 'Swap' ? 'Buchungen getauscht' : 'Buchung verschoben');
      onCompleted?.();
    },
    onError: async (error) => setProblem(reassignmentProblemText(await toApiError(error))),
  });

  const resetCommand = () => {
    setCommandId(operationId());
    setProblem(null);
  };

  return { preview, apply, problem, resetCommand };
}
